/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Adapters: DB row / local / view_id shape <-> NormalizedSavedView.
 */

import type { Database } from "@/lib/supabase/types";

import type { NormalizedSavedView } from "./types";

export function normalizeSavedView<TFilters = Record<string, unknown>>(
  row: Database["public"]["Tables"]["saved_views"]["Row"],
): NormalizedSavedView<TFilters> {
  return {
    id: row.view_id,
    name: row.name,
    view_type: row.view_type,
    filters: (row.filters as TFilters) || ({} as TFilters),
    is_org_preset: row.is_org_preset ?? false,
    is_default: row.is_default ?? false,
    sort_by: row.sort_by,
    sort_order: row.sort_order as "asc" | "desc" | null,
    columns: row.columns as string[] | null,
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
}

export function normalizeLocalSavedView<TFilters = Record<string, unknown>>(
  view: { id: string; name: string; filters: TFilters },
  viewType?: string,
): NormalizedSavedView<TFilters> {
  return {
    id: view.id,
    name: view.name,
    view_type: viewType,
    filters: view.filters,
    is_org_preset: false,
    is_default: false,
  };
}

export function denormalizeSavedView(
  view: NormalizedSavedView,
  organizationId: string,
  userId?: string | null,
): Database["public"]["Tables"]["saved_views"]["Insert"] {
  return {
    view_id: view.id,
    name: view.name,
    view_type: view.view_type ?? "projects",
    filters:
      view.filters as Database["public"]["Tables"]["saved_views"]["Insert"]["filters"],
    organization_id: organizationId,
    user_id: view.is_org_preset ? null : (userId ?? null),
    is_org_preset: view.is_org_preset ?? false,
    is_default: view.is_default ?? false,
    sort_by: view.sort_by ?? null,
    sort_order: view.sort_order ?? null,
    columns: view.columns ?? null,
  };
}

export interface ViewWithViewId<TFilters = Record<string, unknown>> {
  view_id: string;
  name: string;
  filters: TFilters;
  view_type?: string;
  is_org_preset?: boolean;
  is_default?: boolean;
  sort_by?: string | null;
  sort_order?: "asc" | "desc" | null;
  columns?: string[] | null;
  created_at?: string;
  updated_at?: string;
}

export function normalizeViewWithViewId<TFilters = Record<string, unknown>>(
  view: ViewWithViewId<TFilters>,
): NormalizedSavedView<TFilters> {
  return {
    id: view.view_id,
    name: view.name,
    view_type: view.view_type,
    filters: view.filters,
    is_org_preset: view.is_org_preset ?? false,
    is_default: view.is_default ?? false,
    sort_by: view.sort_by,
    sort_order: view.sort_order,
    columns: view.columns,
    created_at: view.created_at,
    updated_at: view.updated_at,
  };
}

export function normalizeViewsWithViewId<TFilters = Record<string, unknown>>(
  views: ViewWithViewId<TFilters>[],
): NormalizedSavedView<TFilters>[] {
  return views.map(normalizeViewWithViewId);
}
